import React from "react";
import { connect } from "react-redux";
import * as R from "ramda";
import { Div, InfoIcon, Span } from "components/Base";
import {
  FIELD_IDS,
  MODULES_TINY_MCE,
  FORMATS_TINY_MCE
} from "EventLight/Sessions/AddSessionModal/constants";
import { Link } from "react-router";
import { setLocalZone } from "utils/General";
import { actions, getters } from "EventLight/Sessions/AddSessionModal/model";
import {
  getEventTimezone,
  eventId as getEventId
} from "redux/modules/event/selectors";
import {
  canEditSessions,
  getFieldsThatHaveTranslations,
  getTranslatedValuesForField
} from "../selectors";
import { Panel } from "EventLight/Common/FieldLayout";
import {
  Input,
  DateTimeInput,
  AdvancedWYSIWYG,
  DropdownAsync
} from "ui-kit/Form/View";

import {
  LanguageVariantsButton,
  LanguageVariantModal
} from "EventLight/Sessions/Page/View/LanguageVariantUI";

import CanUserDo from "components/Global/Security/CanUserDo";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

import css from "./basicDetails.scss";
import CSSModules from "react-css-modules";

const InputWrapperRow = ({ children, ...props }) => {
  return (
    <Div display="row" {...props}>
      {children}
    </Div>
  );
};

const Label = ({ children, tooltip, style = {} }) => {
  return (
    <Div
      color="black"
      fw={3}
      fs={3}
      display="row.flex-start.center"
      style={style}
      mb={2}
    >
      {children}
      {tooltip ? <InfoIcon ml={1} color="gray5" tooltip={tooltip} /> : null}
    </Div>
  );
};

const decorate = R.compose(
  connect(
    state => ({
      data: getters.data(state),
      timezone: getEventTimezone(state),
      eventId: getEventId(state),
      canEdit: canEditSessions(state),
      languageModalIsOpen: getters.languageModalIsOpen(state),
      fieldsThatHaveTranslations: getFieldsThatHaveTranslations(state),
      selectedTranslatedFieldId: getters.selectedTranslatedFieldId(state),
      translatedValues: getTranslatedValuesForField(state)
    }),
    {
      uploadHeaderImg: actions.uploadHeaderImg,
      toggleOnlineSession: actions.toggleOnlineSession,
      removeContent: actions.removeContent,
      reorderContent: actions.reorderContent,
      showContentModal: actions.showContentModal,
      removeContentVideo: actions.removeContentVideo,
      setSelectedTranslatedFieldId: actions.setSelectedTranslatedFieldId,
      setNewData: newData => actions.setData(newData),
      updateTranslatedValue: actions.updateTranslatedValue,
      toggleLanguageModal: value => actions.setLanguageModalIsOpen(value)
    }
  ),
  CSSModules(css)
);

const BasicDetails = ({
  data,
  timezone,
  eventId,
  canEdit,
  languageModalIsOpen,
  toggleLanguageModal,
  fieldsThatHaveTranslations,
  setSelectedTranslatedFieldId,
  selectedTranslatedFieldId,
  translatedValues,
  updateTranslatedValue
  //
}) => {
  return (
    <>
      <Panel>
        <div className={css.row}>
          {data.friendly_id && (
            <div style={{ width: "15%" }}>
              <Label tooltip="Use this ID to reference your sessions">
                Session ID
              </Label>
              <InputWrapperRow>
                <Label>{data.friendly_id}</Label>
              </InputWrapperRow>
            </div>
          )}
          {/* title */}
          <div style={{ width: "85%" }}>
            <Label>
              Title <span style={{ color: "red" }}>*</span>
              {fieldsThatHaveTranslations[FIELD_IDS.TITLE] ? (
                <LanguageVariantsButton
                  tooltip={'Edit language variants for "Title"'}
                  onClickIcon={() => {
                    toggleLanguageModal(true);
                    setSelectedTranslatedFieldId(FIELD_IDS.TITLE);
                  }}
                />
              ) : null}
            </Label>

            {languageModalIsOpen &&
            selectedTranslatedFieldId === FIELD_IDS.TITLE ? null : (
              <Input
                fieldId={FIELD_IDS.TITLE}
                style={{
                  width: "100%"
                }}
                iniValue={R.prop(FIELD_IDS.TITLE)(data)}
                size="big"
                inputType="outline"
                placeholder="Enter a title for your session"
                disabled={!canEdit}
              />
            )}
          </div>
        </div>
        <div className={css.row} style={{ marginBottom: 0 }}>
          {/* status */}
          <div style={{ width: "30%" }}>
            <Label tooltip="Select the status of this session. Only 'Accepted' sessions appear in your agenda.">
              Status
            </Label>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.STATUS}
                iniValue={R.prop(FIELD_IDS.STATUS)(data)}
                multipleSelect={false}
                menuHeight={125}
                style={{ width: "100%" }}
                placeholder="Select a status..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
                isClearable={false}
                useBadgeStyle
                usePortal
              />
            </InputWrapperRow>
          </div>

          <div style={{ width: "20%" }}>
            <Label tooltip="Enter the number of CEU Credits attributed to this session">
              CEU Credits
            </Label>
            <InputWrapperRow display="column">
              <Input
                fieldId={FIELD_IDS.CEU_CREDITS}
                style={{
                  width: "100%"
                }}
                iniValue={R.prop(FIELD_IDS.CEU_CREDITS)(data)}
                size="big"
                inputType="outline"
                placeholder="0.00"
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>

          {/* date */}
          <div style={{ width: "50%" }}>
            <InputWrapperRow display="column">
              <Div display="row.flex-start.center">
                <Div flex={1}>
                  <Label>Starts At</Label>
                  <DateTimeInput
                    fieldId={FIELD_IDS.STARTS_AT}
                    iniValue={setLocalZone(
                      R.propOr(null, FIELD_IDS.STARTS_AT, data),
                      timezone,
                      true
                    )}
                    materialUI
                    modalVariant
                    disabled={!canEdit}
                  />
                </Div>
                <Div ml={2} flex={1}>
                  <Label>Ends At</Label>
                  <DateTimeInput
                    fieldId={FIELD_IDS.ENDS_AT}
                    iniValue={setLocalZone(
                      R.propOr(null, FIELD_IDS.ENDS_AT, data),
                      timezone,
                      true
                    )}
                    materialUI
                    modalVariant
                    disabled={!canEdit}
                  />
                </Div>
              </Div>
            </InputWrapperRow>
          </div>
        </div>
        <div className={css.row}>
          {/* description */}
          <div style={{ width: "100%" }}>
            <Label>
              Description{" "}
              {fieldsThatHaveTranslations[FIELD_IDS.DESCRIPTION] ? (
                <LanguageVariantsButton
                  tooltip={'Edit language variants for "Description"'}
                  onClickIcon={() => {
                    toggleLanguageModal(true);
                    setSelectedTranslatedFieldId(FIELD_IDS.DESCRIPTION);
                  }}
                />
              ) : null}
            </Label>

            <InputWrapperRow bg="white" display="column">
              {languageModalIsOpen &&
              selectedTranslatedFieldId === FIELD_IDS.DESCRIPTION ? null : (
                <AdvancedWYSIWYG
                  fieldId={FIELD_IDS.DESCRIPTION}
                  iniValue={R.propOr("", FIELD_IDS.DESCRIPTION, data)}
                  settings={{
                    height: 350
                  }}
                  modules={MODULES_TINY_MCE}
                  formats={FORMATS_TINY_MCE}
                  disabled={!canEdit}
                />
              )}
            </InputWrapperRow>
          </div>
        </div>
        <div className={css.row}>
          {/* format */}
          <div className={css.halfWidth}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Formats indicate the type, style, and length of the session">
                Format
              </Label>
              <CanUserDo
                action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
              >
                <Link
                  to={`/event-light/${eventId}/sessions/settings/formats`}
                  target="_blank"
                >
                  <Span color="primary7">Manage your formats</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.FORMAT}
                iniValue={R.prop(FIELD_IDS.FORMAT)(data)}
                multipleSelect={false}
                menuHeight={125}
                style={{ width: "100%" }}
                placeholder="Select a format..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>

          {/* tags */}
          <div className={css.halfWidth}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Tag sessions to help attendees discover content">
                Tags
              </Label>{" "}
              <CanUserDo
                action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
              >
                <Link to={`/event-light/${eventId}/tags`} target="_blank">
                  <Span color="primary7">Manage your tags</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.TAGS}
                iniValue={R.prop(FIELD_IDS.TAGS)(data)}
                multipleSelect={true}
                menuHeight={125}
                style={{ width: "100%" }}
                placeholder="Select one or more tags..."
                loadOptionsOnFocus={true}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
        </div>
        <div className={css.row}>
          {/* level */}
          <div className={css.thirdWidth}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Levels indicate which sessions are appropriate for which attendees">
                Level
              </Label>
              <CanUserDo
                action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
              >
                <Link
                  to={`/event-light/${eventId}/sessions/settings/levels`}
                  target="_blank"
                >
                  <Span color="primary7">Manage your levels</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.LEVEL}
                iniValue={R.prop(FIELD_IDS.LEVEL)(data)}
                multipleSelect={false}
                menuHeight={150}
                style={{ width: "100%" }}
                placeholder="Select a level..."
                loadOptionsOnFocus={true}
                menuPlacement={"top"}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
          {/* language */}
          <div className={css.thirdWidth}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Add a language to help attendees find relevant sessions">
                Language
              </Label>
              <CanUserDo
                action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
              >
                <Link
                  to={`/event-light/${eventId}/sessions/settings/languages`}
                  target="_blank"
                >
                  <Span color="primary7">Manage your languages</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.LANGUAGE}
                iniValue={R.prop(FIELD_IDS.LANGUAGE)(data)}
                multipleSelect={false}
                menuHeight={150}
                style={{ width: "100%" }}
                placeholder="Select a language..."
                loadOptionsOnFocus={true}
                menuPlacement={"top"}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>

          {/* track */}
          <div className={css.thirdWidth}>
            <Div mt={2} fs={1} display="row.space-between.center">
              <Label tooltip="Add sessions to a track to organize them by content">
                Track
              </Label>
              <CanUserDo
                action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
              >
                <Link
                  to={`/event-light/${eventId}/sessions/settings/tracks`}
                  target="_blank"
                >
                  <Span color="primary7">Manage your tracks</Span>
                </Link>
              </CanUserDo>
            </Div>
            <InputWrapperRow display="column">
              <DropdownAsync
                fieldId={FIELD_IDS.TRACK}
                iniValue={R.prop(FIELD_IDS.TRACK)(data)}
                multipleSelect={false}
                menuHeight={150}
                style={{ width: "100%" }}
                placeholder="Select a track..."
                loadOptionsOnFocus={true}
                menuPlacement={"top"}
                disabled={!canEdit}
              />
            </InputWrapperRow>
          </div>
        </div>
      </Panel>

      {languageModalIsOpen ? (
        <LanguageVariantModal
          translatedValues={translatedValues}
          languageModalIsOpen={languageModalIsOpen}
          updateTranslatedValue={updateTranslatedValue}
          toggleLanguageModal={toggleLanguageModal}
        />
      ) : null}
    </>
  );
};
export default decorate(BasicDetails);
